import Hls from 'hls.js';
import { observable, computed, action, autorun } from 'mobx';
import { EventEmitter } from 'events';

import messagingService from 'services/MessagingService/MessagingService';
import {
  BUFFERING_OVERLAY,
  HLS_DEBUG,
  HLS_DETACH,
  HLS_LEVEL_LOADED,
  HLS_REBUFFERING,
  HLS_RECONNECT,
  RECONNECTING_OVERLAY,
  SEND_HLS_VIDEO_EVENTS,
  SET_BROADCASTER_VIDEO_LOADING,
} from 'services/MessagingService/EventNames';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';


const X_TO_LAST_ELEMENTS = -5;

class HlsService extends EventEmitter {
  constructor(config) {
    super();
    this.messagingService = messagingService();
    this.messagingService.on(HLS_RECONNECT, this.reconnect);
    this.messagingService.on(HLS_DETACH, this.detach);
    this.config = config;
  }

  destructor() {
    this.messagingService.off(HLS_RECONNECT, this.reconnect);
    this.messagingService.off(HLS_DETACH, this.detach);
  }

  currentTimeInterval = null;
  @observable currentTimeList = [];
  @observable instance = null;
  @observable source = null;
  @observable video = null;
  @observable recconectTries = 0;
  hlsTunedParametersActive = false;
  hlsDebugActive = false;
  hlsTestActive = false;
  @observable lastPlayPos = 0;
  @observable currentPlayPos = 0;
  @observable retries = 0;
  @observable maxRetries = 10;
  @observable reconnectInterval = null;
  @observable repeatedPlaylistTimes = 0;
  @observable lastReconnectTime = null;
  @observable durationArray = [];
  @observable lastPlaylist = null;
  @observable bufferedChunks = 0;

  autoBufferDisposer = null;
  autoBufferDisposerInterval = null;
  autoReconnectDisposer = null;
  autoReconnectDisposerInterval = null;

  @computed get isHlsSupported() {
    return Hls.isSupported();
  }

  init = (source, video) => {
    if (this.instance) {
      this.destroy();
    }
    this.video = video;
    this.hlsDebugActive =
      window.location && window.location.search && window.location.search.indexOf('debug=true') >= 0;
    this.hlsTestActive =
      window.location && window.location.search && window.location.search.indexOf('hslTest=true') >= 0;

    if (Hls.isSupported()) {
      this.source = source;
      const defaultHlsConfig = this.getDefaultConfig();
      this.instance = new Hls(this.config || defaultHlsConfig);
      this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::init HLS', source, instance: this.instance });
      this.attachListeners();
      this.instance?.stopLoad?.();
      this.instance?.detachMedia?.();
      this.instance?.loadSource?.(this.source);
      this.instance?.attachMedia?.(this.video);
      this.automaticReconnect();
    } else {
      this.currentTimeInterval = setInterval(() => {
        const currentTime = Math.floor(this.video.currentTime);
        this.currentTimeList.push(currentTime);
      }, SECOND_AS_MILLISECONDS);
    }
    this.automaticReconnect();

    if (this.autoBufferDisposer) {
      this.autoBufferDisposer();
      this.autoBufferDisposer = null;
    }
    if (this.autoReconnectDisposer) {
      this.autoReconnectDisposer();
      this.autoReconnectDisposer = null;
    }

    this.autoBufferDisposer = autorun(() => {
      let counter = 0;
      clearInterval(this.autoBufferDisposerInterval);
      this.autoBufferDisposerInterval = setInterval(() => {
        if (counter && counter % 10 === 0) {
          this.messagingService.emit(BUFFERING_OVERLAY, { visible: true, duration: 10 });
        }

        if (this.isBuffering) {
          if (counter === 0) {
            this.messagingService.emit(BUFFERING_OVERLAY, { visible: true, duration: 0 });
          }
          counter += 1;
        }

        if (!this.isBuffering && counter !== 0) {
          this.messagingService.emit(BUFFERING_OVERLAY, { visible: false, duration: counter });
          counter = 0;
        }
      }, SECOND_AS_MILLISECONDS);
    });

    this.autoReconnectDisposer = autorun(() => {
      let counter = 0;
      clearInterval(this.autoReconnectDisposerInterval);
      this.autoReconnectDisposerInterval = setInterval(() => {
        if (counter && counter % 10 === 0) {
          this.messagingService.emit(RECONNECTING_OVERLAY, { visible: true, duration: 10 });
        }

        if (this.showReconnect) {
          if (counter === 0) {
            this.messagingService.emit(RECONNECTING_OVERLAY, { visible: true, duration: 0 });
          }
          counter += 1;
        }

        if (!this.showReconnect && counter !== 0) {
          this.messagingService.emit(RECONNECTING_OVERLAY, { visible: false, duration: counter });
          counter = 0;
        }
      }, SECOND_AS_MILLISECONDS);
    });
  };

  getDefaultConfig = () => {
    const defaultHlsConfig = {
      enableWorker: true,
      capLevelOnFPSDrop: true,
      // seconds
      abrEwmaFastLive: 3,
      // seconds
      abrEwmaSlowLive: 5,
      abrBandWidthFactor: 0.95,
      abrBandWidthUpFactor: 0.7,
      // delay
      liveSyncDurationCount: 1,
      // max delay
      liveMaxLatencyDurationCount: 3,
      maxSeekHole: 10,
    };

    if (this.hlsDebugActive) {
      defaultHlsConfig.debug = true;
    }

    if (this.hlsTestActive) {
      defaultHlsConfig.fragLoadingTimeOut = 2000;
      defaultHlsConfig.fragLoadingMaxRetry = 1;
      defaultHlsConfig.fragLoadingRetryDelay = 100;
    }

    return defaultHlsConfig;
  };

  destroy = () => {
    if (this.instance) {
      this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::destroy HLS' });
      this.detach();
      this.detachListeners();
      this.instance.destroy();
    }
    clearInterval(this.currentTimeInterval);
  };

  detach = () => {
    if (this.instance) {
      this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::detach HLS' });
      this.instance?.stopLoad?.();
      this.instance?.detachMedia?.();
      this.clearAutomaticReconnect();
    }
  };

  attachListeners = () => {
    this.instance.on(Hls.Events.LEVEL_LOADED, this.onHlsLevelLoaded);
    this.instance.on(Hls.Events.FRAG_BUFFERED, this.fragBufferedFunc);
  };

  detachListeners = () => {
    this.instance.off(Hls.Events.LEVEL_LOADED, this.onHlsLevelLoaded);
    this.instance.off(Hls.Events.FRAG_BUFFERED, this.fragBufferedFunc);
  };

  @computed get getNumberOfRepeatedPlaylists() {
    const defaultRepeatedCount = 3;
    if (this.durationArray.length) {
      const averageDuration = this.durationArray.reduce((acc, i) => acc + i, 0) / this.durationArray.length;

      // 6 is the number of seconds without new chunks
      return Math.max(defaultRepeatedCount, 6 * (1 / averageDuration));
    }

    return defaultRepeatedCount;
  }

  @computed get showReconnect() {
    if (!Hls.isSupported()) {
      if (this.currentTimeList.length > 10) {
        const lastFiveCurrentTime = this.currentTimeList.slice(X_TO_LAST_ELEMENTS);

        return lastFiveCurrentTime.every((time) => time === lastFiveCurrentTime[0]);
      }
    }

    return this.repeatedPlaylistTimes > this.getNumberOfRepeatedPlaylists;
  }

  @computed get isBuffering() {
    if (Hls.isSupported()) {
      return this.bufferedChunks < 3;
    }

    return false;
  }

  onHlsLevelLoaded = (evName, data) => {
    this.messagingService.emit(HLS_LEVEL_LOADED, data);
    this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);
    const wasRebuferring = this.repeatedPlaylistTimes > this.getNumberOfRepeatedPlaylists;
    if (data.networkDetails.response === this.lastPlaylist) {
      this.repeatedPlaylistTimes += 1;
    } else {
      this.lastPlaylist = data.networkDetails.response;
      this.repeatedPlaylistTimes = 0;
    }
    const isRebuferring = this.repeatedPlaylistTimes > this.getNumberOfRepeatedPlaylists;
    if (isRebuferring) {
      this.messagingService.emit(HLS_REBUFFERING, {
        repeatedPlaylistTimes: this.repeatedPlaylistTimes,
        wasRebuferring,
        isRebuferring,
      });
    }
  };

  @action fragBufferedFunc = (event, data) => {
    this.bufferedChunks += 1;
    if (this.durationArray.length > 10) {
      this.durationArray.shift();
    }
    this.durationArray.push(data.frag.duration);
  };

  /**
   * Triggers attempts to recconect (usually after an error / exception) or a break of stream;
   */
  reconnect = (source) => {
    clearInterval(this.currentTimeInterval);
    this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::reconnect', source });
    // Attempt to reinitialize with the same data
    if (Hls.isSupported()) {
      this.lastReconnectTime = Date.now();
      this.instance?.stopLoad?.();
      this.instance?.detachMedia?.();
      this.instance?.loadSource?.(source || this.source);
      this.instance?.attachMedia?.(this.video);
      this.automaticReconnect();
      this.messagingService.emit(SEND_HLS_VIDEO_EVENTS, { details: 'Reconnect' });
    }
  };

  automaticReconnect = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::start automaticReconnect' });
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
    }
    this.reconnectInterval = setInterval(this.checkBuffering, this.checkInterval);
  };

  clearAutomaticReconnect = () => {
    clearInterval(this.reconnectInterval);
  };

  checkBuffering = () => {
    if (!this.video) {
      this.messagingService.emit(HLS_DEBUG, { method: 'hlsService::checkBuffering - NO VIDEO' });

      return;
    }
    this.currentPlayPos = this.video.currentTime;

    // checking offset, e.g. 1 / 50ms = 0.02
    const offset = 1 / this.checkInterval;

    // if no buffering is currently detected,
    // and the position does not seem to increase
    // and the player isn't manually paused...
    if (this.currentPlayPos < this.lastPlayPos + offset && this.video.readyState >= this.video.HAVE_METADATA) {
      this.messagingService.emit(HLS_DEBUG, {
        method: 'hlsService::checkBuffering retries++',
        crtRetries: this.retries,
        maxRetries: this.maxRetries,
      });
      if (navigator.onLine) {
        this.retries += 1;
      }
      if (this.retries > this.maxRetries) {
        // Cam4Event.dispatch(Cam4Event.hlsPlayer.PLAYER_EVENT, {
        //     "name": "automaticReconnect",
        //     "currentPlayPos": currentPlayPos,
        //     "lastPlayPos": lastPlayPos,
        //     "offset": offset,
        //     "paused": video.paused,
        //     "readyState": video.readyState
        // });
        // eslint-disable-next-line
        console.log('====RECONNECT=====');
        this.reconnect();
        this.retries = 0;
        // data.event = 'automaticReconnect';
        // LoggingService.log(data);
      }
    }

    // if we were buffering but the player has advanced,
    // then there is no buffering
    if (this.currentPlayPos > this.lastPlayPos + offset) {
      if (this.retries > 0) {
        this.messagingService.emit(HLS_DEBUG, {
          method: 'hlsService::checkBuffering reset retries',
          crtRetries: this.retries,
        });
      }
      this.retries = 0;
    }
    this.lastPlayPos = this.currentPlayPos;
  };
}

export default new HlsService();
